import dayjs from "dayjs";

/* Attendance clock-out corrections (owner 10-19): pure helpers shared by the login prompt
   (components/AttendanceCorrection/AttendanceCorrectionHost.jsx) and the approver queue
   (./CorrectionsQueue.jsx). The server (v2 api/timesheets/corrections_core.py) enforces the same
   bounds; these only give the answer before the round trip. Times are clinic-local, no timezone,
   like the timesheet rows ("2026-10-05T18:00:00"). */

export const OPEN_STATUSES = ["awaiting_employee", "pending_approval", "rejected", "no_response"];

export const STATUS_LABEL = {
  awaiting_employee: "Waiting for employee",
  pending_approval: "Needs decision",
  rejected: "Rejected - may resubmit",
  no_response: "No response",
  approved: "Approved",
  rejected_final: "Rejected - recorded time kept",
  cancelled: "Replaced",
};

export const DECISION_LABEL = {
  approve: "Approved",
  set: "Time set by approver",
  keep: "Recorded time kept",
  reject: "Rejected",
};

/* audit_logs arrives as a JSON string, the string "null" (rows an old edit wiped), an object, or
   nothing. JSON.parse("null") is null, so `JSON.parse(x).autoClockout` threw a TypeError. */
export const parseAuditLogs = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "object") return Array.isArray(value) ? null : value;
  if (typeof value !== "string") return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
};

export const isAutoClockout = (auditLogs) =>
  String(parseAuditLogs(auditLogs)?.autoClockout ?? "").toUpperCase() === "Y";

export const clockLabel = (iso) => (iso ? dayjs(iso).format("h:mm A") : "-");
export const dayLabel = (isoDate) => (isoDate ? dayjs(isoDate).format("ddd, MMM D") : "");
export const dateTimeLabel = (iso) => (iso ? dayjs(iso).format("ddd, MMM D h:mm A") : "");
export const hhmm = (iso) => (iso ? dayjs(iso).format("HH:mm") : "");

/* The cron's 11:59 PM closure (a clock-in at/after 4 PM): not a real clock-out (owner 10-19), so
   neither "11:59 PM is correct" nor the approver's "Keep" is offered; the server refuses them too. */
export const isPlaceholderOut = (item) =>
  item?.recorded_is_placeholder === true ||
  (item?.recorded_is_placeholder === undefined && hhmm(item?.recorded_clock_out) === "23:59");

export const PLACEHOLDER_MESSAGE =
  "11:59 PM is the time the system closes a shift automatically. Enter the time you actually clocked out.";

const TIME_RE = /^(\d{1,2}):(\d{2})$/;

/* "HH:mm" from an <input type="time"> -> "YYYY-MM-DDTHH:mm:00" on the clock-in's day (the
   clock-out is the same day by rule, so the form never asks for a date). "" when unusable. */
export const clockOutValue = (item, value) => {
  const m = TIME_RE.exec(String(value || "").trim());
  if (!m || !item?.clock_in) return "";
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return "";
  return `${dayjs(item.clock_in).format("YYYY-MM-DD")}T${String(h).padStart(2, "0")}:${m[2]}:00`;
};

/* The owner's bounds: after the clock-in, the same day, not in the future, not past the next
   clock-in that day. Returns the problem to show, or "" when the time is fine. */
export const clockOutProblem = (item, value, now = dayjs()) => {
  const iso = clockOutValue(item, value);
  if (!iso) return "Enter the time you clocked out.";
  const out = dayjs(iso);
  const cin = dayjs(item.clock_in);
  if (!out.isAfter(cin)) return `The clock-out must be after the clock-in (${clockLabel(item.clock_in)}).`;
  if (out.isAfter(now)) return "The clock-out cannot be in the future.";
  if (item.next_clock_in && out.isAfter(dayjs(item.next_clock_in))) {
    return `The clock-out must be at or before the next clock-in that day (${clockLabel(item.next_clock_in)}).`;
  }
  return "";
};

/* Hours change of a clock-out against the recorded one: "+0:45", "-1:10", "0:00". */
export const hoursDelta = (recordedIso, newIso) => {
  if (!recordedIso || !newIso) return "";
  const minutes = dayjs(newIso).diff(dayjs(recordedIso), "minute");
  const sign = minutes > 0 ? "+" : minutes < 0 ? "-" : "";
  const abs = Math.abs(minutes);
  return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, "0")}`;
};

/* The employee may answer now (the server's can_submit, plus a stale-tab guard on respond_by). */
export const canAnswer = (item, now = dayjs()) =>
  !!item?.can_submit && (!item.respond_by || !dayjs(item.respond_by).isBefore(now));
